import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import "core-js/modules/es6.regexp.replace.js";

/**
 * Utility for handling trk codes in URLs. Contains
 * <ul>
 *  <li>
 *    enums for standardized trk elements (but not for bluekai dependant and free-form elements)
 *  </li>
 *  <li>utility to get trk_ field values from a taxonomy object</li>
 *  <li>utility to set trk elements in a given url</li>
 * </ul>
 *
 * @module trk-helper
 * @see https://nerdwallet.atlassian.net/wiki/pages/viewpage.action?pageId=79987056
 */
import qs from 'query-string'; // eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration

import pickBy from 'lodash/pickBy';

function warn() {
  if (process.env.NODE_ENV === 'development') {
    var _console;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    (_console = console).warn.apply(_console, ['[trk-helper]'].concat(args));
  }
}
/**
trk tag names
@enum {string}
*/


export var TrkTag = {
  // The type of integration used
  mode: 'trk_mode',
  // The app that generated the registration
  channel: 'trk_channel',
  // The page that generated the registration (which vertical it belongs to)
  vertical: 'trk_vertical',
  // The page that generated the registration (which topic it belongs to)
  topic: 'trk_topic',
  // The page that generated the registration (which type of page template / format)
  pageType: 'trk_pagetype',
  // The page that generated the registration (which part of the content funnel)
  nldt: 'trk_nldt',
  // The page element that generated the registration (the format of the registration CTA)
  element: 'trk_element',
  // The page element that generated the registration (where the registration CTA is on the page)
  location: 'trk_location',
  // The page element that generated the registration (the text on the registration CTA)
  copy: 'trk_copy',
  // The page section context that generated the registration
  // (where is the containing context of the registration CTA)
  sectionCategory: 'trk_sectionCategory',
  // The cohort that generated the registration (which cohort/experience the user belongs to)
  format: 'trk_format'
};
/**
trk_mode
@enum {string}
*/

export var TrkMode = {
  redirect: 'redirect',
  embed: 'embed',
  modal: 'modal'
};
/**
trk_channel
@enum {string}
*/

export var TrkChannel = {
  email: 'email',
  mobile: 'mobile',
  web: 'web'
};
/**
trk_pagetype
@enum {string}
*/

export var TrkPageType = {
  productDetail: 'product_detail',
  marketPlace: 'marketplace',
  preQual: 'prequal',
  article: 'article',
  roundUp: 'roundup',
  calculator: 'calculator',
  review: 'review',
  hub: 'hub'
};
/**
trk_element
@enum {string}
*/

export var TrkElement = {
  banner: 'banner',
  button: 'button',
  hyperlink: 'hyperlink',
  adUnit: 'ad_unit',
  filter: 'filter',
  application: 'application',
  image: 'image'
};
/**
Get any default trk values that we can figure out using taxonomy or otherwise.

@private
@param {Object} taxonomy - Page taxonomy object usually found in server.js or window.nwPageTaxonomy
@return {Object} - default trk values at this time
*/

export function getDefaultTrk() {
  var _pickBy;

  var taxonomy = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return pickBy((_pickBy = {}, _defineProperty(_pickBy, TrkTag.vertical, taxonomy.vertical), _defineProperty(_pickBy, TrkTag.topic, taxonomy.topic), _defineProperty(_pickBy, TrkTag.nldt, taxonomy.pageType), _defineProperty(_pickBy, TrkTag.channel, TrkChannel.web), _pickBy)); // default filter function is _.identity
}
/**
 * <p>
 * Sets various track parameters into the provided url. It will try to extract some values (see
 * README.md) from taxonomy if a taxonomy object is provided.
 * </p>
 * <p>
 * Note: Most apps set page taxonomy in server.js as one of the properties to nlOpts. It is also
 * available on the window but you won't access to the window object when rendering on the server.
 * </p>
 * @param {Object} options
 * @param {string} [options.url = ''] - URL which needs the TRK
 * @param {Object} [options.params] - An object containing various TRK items
 * @param {Object} [options.taxonomy] - Page taxonomy object for the current page
 *
 * @returns {string} - URL with the supplied TRK values
 * @example
 * ```
 * import taxonomy from 'my-app-specific-taxonomy';
 *
 * const redirect_uri = setTrk({
 *     url: '/home/register?redirect_uri=%2FmyAwesomePage&flow=credit-score&foo=bar',
 *     params: {
 *       [TrkTag.element]: TrkElement.Hyperlink,
 *       [TrkTag.Location]: 'cc-filters'
 *     },
 *     taxonomy
 *   });
 * ```
 */

export function setTrk() {
  var url;
  var taxonomy;
  var params; // The following code can be made less verbose, but I want to keep it very easy to distinguish
  // between the two invocation styles.

  if (typeof (arguments.length <= 0 ? undefined : arguments[0]) === 'string') {
    // the older signature was setTrk(url: string, params: object)
    warn('Deprecated invocation of setTrk. trk_ values will only be auto-populated on the client'); // eslint-disable-next-line prefer-destructuring -- directive added automatically by Shepherd migration

    url = arguments.length <= 0 ? undefined : arguments[0]; // you may not have a window, or you may have one and not have taxonomy on it.

    taxonomy = (typeof window === 'undefined' ? null : window.nwPageTaxonomy) || {};
    params = (arguments.length <= 1 ? undefined : arguments[1]) || {};
  } else {
    // The new signature is setTrk(options: object)
    var options = arguments.length <= 0 ? undefined : arguments[0];
    url = options.url || '';
    taxonomy = options.taxonomy || {};
    params = options.params || {};
  }

  var queryString = qs.extract(url);

  var query = _objectSpread(_objectSpread(_objectSpread({}, qs.parse(queryString)), getDefaultTrk(taxonomy)), params);

  var stub = url.replace(/(\?.*)/, '');
  return "".concat(stub, "?").concat(qs.stringify(query));
}